/**
 * "Saved for offline" badge next to a song (display only): saved, downloading, update available,
 * waiting or stopped. Renders nothing for songs that are not part of any offline pack.
 */
import { cx } from '../../components/ui';
import { useT } from '../../i18n';
import { BADGE_KEY } from '../../offline/format';
import { usePack, useSongOfflineState } from '../../offline/hooks';
import { SONG_STATE_ICON, SONG_STATE_TONE } from '../../offline/SaveOfflineButton';
import type { SongOfflineState } from '../../offline/store';
import type { PackKind } from '../../offline/types';

export function OfflineBadge({ songId, className }: { songId: string; className?: string }) {
  const t = useT();
  const state = useSongOfflineState(songId);
  if (!state) return null;
  const Icon = SONG_STATE_ICON[state];
  const label = t(BADGE_KEY[state]);
  return (
    <span className={cx('inline-flex shrink-0 items-center', SONG_STATE_TONE[state], className)} title={label}>
      <Icon className={cx('size-5', state === 'downloading' && 'animate-spin')} aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

const PACK_STATE: Record<'pending' | 'downloading' | 'complete' | 'update' | 'error', SongOfflineState> = {
  pending: 'pending',
  downloading: 'downloading',
  complete: 'saved',
  update: 'update',
  error: 'error',
};

/** Offline state of a whole pack (e.g. a saved setlist in the collections list). */
export function PackBadge({ kind, refId, className }: { kind: PackKind; refId: string | null; className?: string }) {
  const t = useT();
  const pack = usePack(kind, refId);
  if (!pack) return null;
  const state = PACK_STATE[pack.status];
  const Icon = SONG_STATE_ICON[state];
  const label = t(BADGE_KEY[state]);
  return (
    <span className={cx('inline-flex shrink-0 items-center', SONG_STATE_TONE[state], className)} title={label}>
      <Icon className={cx('size-5', state === 'downloading' && 'animate-spin')} aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}
